"use client";

/**
 * LocaleProvider — single source of truth for the active UI locale on
 * the client. Replaces the previous "useState('en') + useEffect(loadLocale)"
 * pattern in `useLocale.ts`, which caused a visible flash on every page
 * load: SSR shipped the EN catalog, hydration ran, then re-rendered in
 * ES ~750 ms later.
 *
 * Now the server (RSC layout) resolves the locale via cookie/header
 * (`getServerLocale()`) and hands it to this provider's `initial`
 * prop. The provider seeds its state with that value so the very
 * first client render already uses the correct catalog — zero flash,
 * zero hydration mismatch.
 *
 * The provider also listens to `frenflow:locale-change` so the
 * existing `LanguageSelector` can keep dispatching the same event
 * after `saveLocale()` writes cookie + localStorage. Backwards-compat
 * with every consumer of `useT()` / `useLocale()`.
 */

import { createContext, useEffect, useState, useContext } from "react";
import type { Locale } from "./LanguageSelector";

const LocaleContext = createContext<Locale>("en");

export function LocaleProvider({
  initial,
  children,
}: {
  initial: Locale;
  children: React.ReactNode;
}) {
  // Seeded from the server-resolved cookie. No useEffect-driven
  // re-render on mount — the value is already correct in SSR HTML.
  const [locale, setLocale] = useState<Locale>(initial);

  useEffect(() => {
    // Stay in sync when the user flips language via the header. The
    // event is dispatched by `LanguageSelector.saveLocale()` AFTER
    // it has already written the cookie + localStorage, so the next
    // SSR request will also see the new value.
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ locale: Locale }>).detail;
      if (detail?.locale && (detail.locale === "en" || detail.locale === "es")) {
        setLocale(detail.locale);
      }
    };
    window.addEventListener("frenflow:locale-change", onChange);
    return () => window.removeEventListener("frenflow:locale-change", onChange);
  }, []);

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

/**
 * Reactive accessor consumed by `useT()` / `useTCategory()` /
 * `useTKalshiSubcat()`. Returns the locale from context — never
 * touches localStorage on render, so SSR and CSR agree from the start.
 */
export function useLocaleContext(): Locale {
  return useContext(LocaleContext);
}
